import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Button,
  Input,
  Label,
  Spinner,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { ErrorCircle20Filled } from '@fluentui/react-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ApiClientError } from '../api/client.js';
import { login, type AuthBrief } from '../api/auth.js';
import { useAuthStore } from '../stores/authStore.js';
import { isPublicDemoHost } from '../lib/publicDemo.js';
import { tomarRutaDeRegreso } from '../lib/sesionCerrada.js';
import { rutaDeVerificacion } from '../lib/verificarAcceso.js';
import { brandingProductoActivo, nombreProductoActivo } from '../lib/brandingDelProducto.js';

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: '1fr',
    placeItems: 'center',
    padding: 'var(--space-xxl)',
    position: 'relative',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-2xl)',
    padding: 'var(--space-4xl)',
    boxShadow: 'var(--shadow-28)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xxl)',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-l)',
  },
  logo: {
    width: '52px',
    height: '52px',
    borderRadius: 'var(--radius-lg)',
    background: 'linear-gradient(135deg, var(--opus-brand-60), var(--opus-brand-40))',
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: '20px',
    color: 'white',
    boxShadow: 'var(--glow-brand)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-600)',
    margin: 0,
    letterSpacing: '-0.01em',
    textAlign: 'center',
  },
  sub: {
    fontSize: 'var(--fs-300)',
    color: 'var(--text-tertiary)',
    margin: 0,
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  // Fluent pone cursor:not-allowed en :disabled; lo pisamos (hub usa wait al enviar).
  submitBtn: {
    cursor: 'pointer',
    ':hover': { cursor: 'pointer' },
    ':disabled': { cursor: 'wait', opacity: 0.85 },
  },
  errorAlert: {
    // Reemplaza Fluent MessageBar (que aplica line-clamp y rompe wrap en
    // mensajes largos). Estilo manual con tokens del design system.
    width: '100%',
    padding: 'var(--space-m) var(--space-l)',
    borderRadius: 'var(--radius-md)',
    background: 'rgba(232, 17, 35, 0.08)',
    border: '1px solid rgba(232, 17, 35, 0.35)',
    color: 'var(--text-primary)',
    fontSize: 'var(--fs-300)',
    lineHeight: 1.5,
    display: 'flex',
    gap: 'var(--space-s)',
    alignItems: 'flex-start',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  errorAlertIcon: {
    flexShrink: 0,
    marginTop: '2px',
    color: '#e81123',
  },
  hint: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
  },
  tenantList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  tenantBtn: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: 'var(--space-l) var(--space-xl)',
    background: 'var(--bg-raised)',
    ...shorthands.border('1px', 'solid', 'var(--border-subtle)'),
    borderRadius: 'var(--radius-lg)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--fs-300)',
    transition: 'all var(--duration-fast) var(--easing-curve)',
    ':hover': {
      ...shorthands.borderColor('var(--opus-brand-60)'),
      background: 'var(--bg-overlay)',
    },
  },
  tenantBtnSlug: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    marginLeft: 'var(--space-s)',
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 'var(--space-l)',
  },
  blockIcon: {
    width: '62px',
    height: '62px',
    borderRadius: '50%',
    background: 'rgba(232, 17, 35, 0.12)',
    border: '1px solid rgba(232, 17, 35, 0.4)',
    display: 'grid',
    placeItems: 'center',
    color: '#e81123',
  },
  blockTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-500)',
    margin: 0,
  },
  blockText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
    lineHeight: 1.55,
    margin: 0,
  },
  footer: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textAlign: 'center',
    padding: '0 var(--space-l)',
  },
});

const RENEW_URL = 'https://ecosoft.com.mx/#contacto';

type Stage = 'credentials' | 'select-tenant' | 'blocked';

export function LoginPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const status = useAuthStore((s) => s.status);

  const [stage, setStage] = useState<Stage>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockMessage, setBlockMessage] = useState<string | null>(null);
  const [tenants, setTenants] = useState<AuthBrief[]>([]);

  // Si vienen desde el dropdown de empresas (AppTopBar onSwitchTo), llegan
  // con `?tenant=<slug>` para que al loguear se valide directamente esa
  // empresa contra Anywhere (sin pasar por el multi-tenant picker).
  const [searchParams] = useSearchParams();
  const targetTenantSlug = searchParams.get('tenant') ?? undefined;
  const reason = searchParams.get('reason') ?? undefined;

  /**
   * A dónde va la persona apenas entra. Normalmente al inicio; pero si llegó acá porque
   * se le cerró la sesión en medio del trabajo, vuelve a la pantalla en la que estaba.
   * Perder la sesión no debería costar además reencontrar dónde estabas.
   *
   * El `ref` es lo que hace que la respuesta no cambie entre llamadas: la dirección se
   * lee y se borra UNA vez. Sin él, la segunda llamada encontraba vacío y mandaba al
   * inicio — que es exactamente lo que pasaba al probarlo en el navegador.
   */
  const recuerdoDelRegreso = useRef<{ valor: string | null }>({ valor: null });
  const aDondeVolver = useCallback(
    () => tomarRutaDeRegreso(typeof sessionStorage === 'undefined' ? null : sessionStorage, recuerdoDelRegreso.current),
    [],
  );

  // Sitio demo público: no hay login; AuthBootstrap emite guest y redirigimos.
  // (La salida de la demostración no vive aquí: son `?demo=0`, el enlace de la
  // pantalla de entrada y "Salir de la demostración" en el menú. Rebotar desde
  // el login sacaba del demo justo al entrar, porque el recorrido pasa por
  // esta ruta.)
  useEffect(() => {
    if (isPublicDemoHost()) {
      navigate('/projects', { replace: true });
    }
  }, [navigate]);

  // Si ya hay sesión activa al montar → redirigir a /
  useEffect(() => {
    if (status === 'authenticated') {
      navigate(aDondeVolver(), { replace: true });
    }
  }, [status, navigate, aDondeVolver]);

  // Si llegamos aquí por un SSO fallido (p.ej. desde el hub), explicamos el motivo
  // en vez de dejar al usuario en un login "vacío" que parece un loop.
  useEffect(() => {
    if (!reason) return;
    const map: Record<string, string> = {
      sso_invalid: 'No pudimos validar tu acceso desde el hub. Inicia sesión aquí para continuar.',
      sso_scope_invalid:
        `No pudimos identificar el equipo con el que intentas entrar. Vuelve a abrir ${nombreProductoActivo()} desde el portal y elige el equipo.`,
      no_programs: `Tu organización no tiene ${nombreProductoActivo()} habilitado. Contacta a soporte.`,
      expired: 'Tu sesión expiró. Vuelve a iniciar sesión.',
      otp_no_disponible: 'No pudimos mandarte el código de verificación. Intenta de nuevo en unos minutos.',
      relogin_tenant:
        `Entra con esta empresa para que validemos tu licencia de ${nombreProductoActivo()} y puedas trabajar en ella.`,
    };
    setError(map[reason] ?? 'No se pudo iniciar sesión automáticamente. Inténtalo de nuevo.');
  }, [reason]);

  async function attemptLogin(tenantSlug?: string) {
    setSubmitting(true);
    setError(null);
    try {
      // Si el usuario llegó con ?tenant=<slug> (re-login forzado por
      // switch desde el topbar), pasamos ese slug al backend para
      // saltearnos el picker y re-validar directamente esa empresa.
      const explicitSlug = tenantSlug ?? targetTenantSlug;
      const result = await login({ username, password, tenantSlug: explicitSlug });
      if (result.kind === 'multiTenant') {
        setTenants(result.data.tenants);
        setStage('select-tenant');
      } else if (result.kind === 'otp') {
        // Equipo nuevo o cuenta de staff: falta el código que llegó por correo.
        navigate(rutaDeVerificacion({ desafio: result.data.desafio, destino: result.data.destino, volver: aDondeVolver() }), { replace: true });
      } else if (result.kind === 'noPlan') {
        // login() ya redirige a apps.opus.com.mx a comprar Start; avisamos por si acaso.
        setError(`Necesitas un plan activo para usar ${nombreProductoActivo()}. Te llevamos a apps.opus.com.mx…`);
      } else {
        setSession(result.data);
        navigate(aDondeVolver(), { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'OPUS_SUBSCRIPTION_INACTIVE') {
        // Misma UX que el hub (pruebas-hub): pantalla de bloqueo con Renovar / Volver.
        setBlockMessage(err.message);
        setStage('blocked');
        setError(null);
      } else if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Error inesperado. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Ingresa usuario y contraseña.');
      return;
    }
    void attemptLogin();
  }

  function backFromBlocked() {
    setStage(tenants.length > 0 ? 'select-tenant' : 'credentials');
    setBlockMessage(null);
    setError(null);
  }

  // Marca del producto por hostname: en ci el login dice "OPUS ci" con su magenta
  // (misma fuente que el favicon y el topbar). ai queda con su azul del CSS.
  const marca = brandingProductoActivo();

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div
            className={styles.logo}
            style={marca.producto === 'ci' ? { background: `linear-gradient(135deg, ${marca.c1}, ${marca.c2})` } : undefined}
          >
            {marca.letras}
          </div>
          <h1 className={styles.title}>{marca.nombre}</h1>
          <p className={styles.sub}>
            {stage === 'credentials'
              ? 'Inicia sesión para continuar'
              : stage === 'select-tenant'
                ? 'Selecciona la organización con la que vas a trabajar'
                : 'Licencia requerida'}
          </p>
        </div>

        {error && stage !== 'blocked' && (
          <div className={styles.errorAlert} role="alert">
            <ErrorCircle20Filled className={styles.errorAlertIcon} />
            <span>{error}</span>
          </div>
        )}

        {stage === 'blocked' ? (
          <div className={styles.block}>
            <div className={styles.blockIcon} aria-hidden>
              <ErrorCircle20Filled />
            </div>
            <h2 className={styles.blockTitle}>Tu licencia OPUS 27 venció</h2>
            <p className={styles.blockText}>
              {blockMessage ??
                `Para usar ${nombreProductoActivo()} necesitas una licencia vigente. Renuévala con tu distribuidor.`}
            </p>
            <Button
              appearance="primary"
              size="large"
              as="a"
              href={RENEW_URL}
              target="_blank"
              rel="noreferrer"
            >
              Renovar / contactar a Ecosoft
            </Button>
            <Button appearance="subtle" onClick={backFromBlocked}>
              ← Volver
            </Button>
          </div>
        ) : stage === 'credentials' ? (
          <form className={styles.form} onSubmit={onSubmit}>
            <div className={styles.field}>
              <Label htmlFor="username" weight="semibold">
                Usuario
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(_, d) => setUsername(d.value)}
                autoFocus
                autoComplete="username"
                disabled={submitting}
                placeholder="usuario@empresa.com"
              />
            </div>
            <div className={styles.field}>
              <Label htmlFor="password" weight="semibold">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(_, d) => setPassword(d.value)}
                autoComplete="current-password"
                disabled={submitting}
              />
            </div>

            <Button
              className={styles.submitBtn}
              appearance="primary"
              size="large"
              type="submit"
              disabled={submitting}
              aria-busy={submitting}
              icon={submitting ? <Spinner size="tiny" /> : undefined}
            >
              {submitting ? 'Verificando…' : 'Entrar'}
            </Button>
          </form>
        ) : (
          <div className={styles.tenantList}>
            {tenants.map((t) => (
              <button
                key={t.slug}
                type="button"
                className={styles.tenantBtn}
                disabled={submitting}
                onClick={() => void attemptLogin(t.slug)}
              >
                <strong>{t.name}</strong>
                {t.name !== t.slug && (
                  <span className={styles.tenantBtnSlug}>· {t.slug}</span>
                )}
              </button>
            ))}
            <Button
              appearance="subtle"
              onClick={() => {
                setStage('credentials');
                setTenants([]);
                setError(null);
              }}
            >
              Volver
            </Button>
          </div>
        )}

        <div className={styles.footer}>
          {marca.nombre} · Ecosoft
        </div>
      </div>
    </div>
  );
}
